import type React from "react";
import { useEffect } from "react";
import { Link, useParams } from "react-router";
import { APP_ROUTES } from "@/pages/routes";
import BlogLayout from "./BlogLayout";
import { getBlogPost } from "./posts";

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPost(slug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!post) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-white dark:bg-gray-950">
        <h1 className="mb-4 font-bold text-2xl text-gray-900 dark:text-white">
          Post not found
        </h1>
        <p className="mb-6 text-gray-600 dark:text-gray-300">
          The blog post you're looking for doesn't exist.
        </p>
        <Link
          to="/blog"
          className="font-medium text-violet-600 transition hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300"
        >
          ← Back to Blog
        </Link>
      </div>
    );
  }

  const PostComponent = post.component;

  return (
    <div className="flex min-h-screen w-full flex-col bg-white dark:bg-gray-950">
      {/* Header */}
      <header className="border-gray-200 border-b bg-white/80 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/80">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to={APP_ROUTES.SPLASH}>
            <span className="font-bold text-gray-900 text-xl dark:text-white">
              Mycen<span className="text-violet-600 dark:text-violet-400">AI</span>
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/blog"
              className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400"
            >
              Blog
            </Link>
            <Link
              to={APP_ROUTES.RESOURCES}
              className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400"
            >
              Resources
            </Link>
            <Link
              to={APP_ROUTES.ABOUT}
              className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400"
            >
              About
            </Link>
            <a
              href="https://confideai.ai/welcome/mycenai"
              className="rounded-lg bg-violet-600 px-6 py-2.5 font-semibold text-sm text-white shadow-sm transition hover:bg-violet-700"
            >
              Get Started
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <BlogLayout post={post}>
          <PostComponent />
        </BlogLayout>
      </main>

      {/* Footer */}
      <footer className="border-gray-200 border-t bg-gray-50 px-6 py-8 dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 text-center">
          <p className="text-gray-500 text-sm dark:text-gray-400">
            © 2026 MycenAI. All rights reserved.
          </p>
          <div className="flex gap-6 text-gray-500 text-sm dark:text-gray-400">
            <Link to={APP_ROUTES.FAQ} className="transition hover:text-violet-600 dark:hover:text-violet-400">
              FAQ
            </Link>
            <Link to={APP_ROUTES.PRIVACY} className="transition hover:text-violet-600 dark:hover:text-violet-400">
              Privacy
            </Link>
            <Link to={APP_ROUTES.TERMS} className="transition hover:text-violet-600 dark:hover:text-violet-400">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostPage;
